import { ListFilterParams } from "components/typings";
import { useTranslation } from "react-i18next";
import { getCatToolName, TaskBase } from "pages/project/common/taskBase.typing";
import Badge from "components/Badge";
import Collapse from "components/Collapse";
import { TaskListTable } from "./TaskListTable";
import { TaskListType } from "../task.typing";

interface GroupedTaskListTableProps {
  tasks: TaskBase[];
  shownDetailDataId?: string;
  filterParams: ListFilterParams<TaskBase>;
  isUserSupplier: boolean;
  taskListType: TaskListType;
}

export function GroupedTaskListTable({
  tasks,
  shownDetailDataId,
  filterParams,
  isUserSupplier,
  taskListType,
}: GroupedTaskListTableProps) {
  const { t } = useTranslation();

  // Group tasks by customer name, CAT tool, task type, and language pair
  const groupedTasks = tasks.reduce(
    (acc, offer) => {
      const catToolName = getCatToolName(offer.catToolId) || t("task-base.fields.noCatTool");
      const languagePair = `${offer.sourceLanguage} → ${offer.targetLanguage}`;
      const groupKey = `${offer.customerName}|${catToolName}|${offer.taskType}|${languagePair}`;

      if (!acc[groupKey]) {
        acc[groupKey] = {
          customerName: offer.customerName,
          catTool: catToolName,
          taskType: offer.taskType,
          languagePair,
          tasks: [],
        };
      }
      acc[groupKey].tasks.push(offer);
      return acc;
    },
    {} as Record<
      string,
      { customerName: string; catTool: string; taskType: string; languagePair: string; tasks: TaskBase[] }
    >
  );

  return (
    <div className="space-y-6">
      {Object.entries(groupedTasks).map(([groupKey, groupData]) => (
        <div
          key={groupKey}
          className="text-color-base bg-color-base rounded-md border border-slate-200 p-4 dark:border-slate-800"
        >
          <Collapse
            header={
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold">{groupData.customerName}</h3>
                  <div className="flex gap-2">
                    <Badge className="text-xs font-semibold">{groupData.catTool}</Badge>
                    <Badge className="text-xs font-semibold">{groupData.taskType}</Badge>
                    <Badge className="text-xs font-semibold">{groupData.languagePair}</Badge>
                  </div>
                </div>
              </div>
            }
            defaultOpen={true}
          >
            <div className="mt-4">
              <TaskListTable
                key={groupKey}
                tasks={groupData.tasks}
                shownDetailDataId={shownDetailDataId}
                filterParams={filterParams}
                isUserSupplier={isUserSupplier}
                taskListType={taskListType}
              />
            </div>
          </Collapse>
        </div>
      ))}
    </div>
  );
}
